import { memo, useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';

import type {
  ControlDescriptor,
  GetFormFunction,
  GetPlaceholderFunction,
  GroupedTableClassNames,
  GroupRowState,
  RearrangeHandler,
  RearrangeHandlerParams,
  RowState,
  WithMeta,
} from './types';

import PlainList from './PlainList';
import GroupWithChildren from './GroupWithChildren';
import { getGroupedListClassNames, rearrange } from './functions';

import './GroupedList.scss';

export interface BaseGroupedListProps<G, I> {
  plain?: boolean;
  dndEnabled?: boolean;
  hideEmpty?: boolean;
  placeholder?: ReactElement;
  classNames?: Partial<GroupedTableClassNames>;
  selectedItem?: I;
  itemControls?: ControlDescriptor<I, G>[];
  groupControls?: ControlDescriptor<G, G>[];
  getItemGroupId: (item: I) => string | number;
  getItemId: (item: I) => number;
  getGroupId: (group: G) => number;
  getItemLabel: (item: I) => ReactElement | string;
  getItemError?: (error: string, item: I) => ReactElement | string;
  getGroupLabel: (group: G) => ReactElement | string;
  getGroupError?: (error: string, group: G) => ReactElement | string;
  getItems?: (items: I[]) => I[];
  getGroups?: (groups: G[]) => G[];
  getItemForm?: GetFormFunction<I, RowState>;
  getGroupForm?: GetFormFunction<G, GroupRowState>;
  getItemPlaceholder?: GetPlaceholderFunction<I, RowState>;
  isItemSortable?: (item: I) => boolean;
}

export interface GroupedListProps<G, I> extends BaseGroupedListProps<G, I> {
  items: I[];
  groups: G[];
  itemStates?: { [key: number]: RowState };
  groupStates?: { [key: number]: GroupRowState };
  onExpand?: (group: G) => void;
  onCollapse?: (group: G) => void;
  onRearrange?: RearrangeHandler;
  onShowUpdateGroupForm?: (group: G) => void;
  onShowUpdateItemForm?: (item: I) => void;
  updateItemState: (item: I, updates: Partial<RowState>) => void;
  updateGroupState?: (group: G, updates: Partial<GroupRowState>) => void;
}

const GroupedList = <G, I>({
  items,
  groups,
  plain,
  dndEnabled,
  hideEmpty,
  placeholder,
  selectedItem,
  classNames: classNamesFromProps,
  itemStates,
  groupStates,
  itemControls,
  groupControls,
  getItemId,
  getGroupId,
  getItemForm,
  getItemLabel,
  getItemGroupId,
  getItemPlaceholder,
  getGroupForm,
  getGroupLabel,
  getGroupError,
  getItems,
  getGroups,
  onExpand,
  onCollapse,
  onRearrange: onRearrangeFromProps,
  updateItemState,
  updateGroupState,
  isItemSortable,
}: GroupedListProps<G, I>) => {
  const [dragInProcess, setDragInProcess] = useState(false);

  const [groupsWithChildren, setGroupsWithChildren] = useState<(G & { children: WithMeta<I>[] })[]>();

  const classNames = useMemo(() => getGroupedListClassNames(classNamesFromProps), [classNamesFromProps]);

  useEffect(() => {
    if (plain) {
      return undefined;
    }

    const preparedGroups = typeof getGroups === 'function' ? getGroups(groups) : groups;

    const groupedItems =
      items?.reduce((acc, item) => {
        const groupId = getItemGroupId(item) || null;
        acc[groupId] = acc[groupId] || [];
        acc[groupId].push(item);
        return acc;
      }, {}) || {};

    const result = preparedGroups.map((group) => {
      const groupId = getGroupId(group) || null;
      const children = groupedItems[groupId];
      return {
        ...group,
        children: getItems(children),
      };
    });

    setGroupsWithChildren(result);
  }, [plain, items, groups, getGroupId, getItemGroupId, getItems, getGroups]);

  const plainGroup = useMemo(
    () => ({
      children: getItems(items),
    }),
    [items, getItems],
  );

  const onRearrange = useCallback(
    (params: RearrangeHandlerParams) => {
      const { itemId, prevGroupId, nextGroupId, prevIndex, nextIndex, isDragging } = params;

      setDragInProcess(isDragging);

      if (!dndEnabled) {
        return;
      }

      if (prevGroupId === nextGroupId && prevIndex === nextIndex) {
        return;
      }

      const item = items.find((item) => getItemId(item) === itemId);

      const updatedItem: WithMeta<I> = {
        ...item,
        sortOrder: nextIndex,
        __meta__: {
          isDragging,
        },
      };

      const updatedGroups = rearrange(groupsWithChildren, updatedItem, nextIndex, nextGroupId, getItemId, getGroupId);

      if (isDragging) {
        setGroupsWithChildren(updatedGroups);
        return;
      }

      delete updatedItem.__meta__;

      const prevGroup = updatedGroups.find((g) => getGroupId(g) === prevGroupId);
      const nextGroup = updatedGroups.find((g) => getGroupId(g) === nextGroupId);

      const prevGroupOrder = prevGroup?.children?.map(getItemId);
      const nextGroupOrder = nextGroup?.children?.map(getItemId);

      if (typeof onRearrangeFromProps === 'function') {
        onRearrangeFromProps({
          itemId,
          prevGroupId,
          nextGroupId,
          prevIndex,
          nextIndex,
          prevGroupOrder,
          nextGroupOrder,
        });
      }
    },
    [items, dndEnabled, groupsWithChildren, getItemId, getGroupId, onRearrangeFromProps],
  );

  const showPlaceholder = !items?.length;

  const content = plain ? (
    <PlainList
      groups={groups}
      groupId={null}
      dndEnabled={dndEnabled}
      classNames={classNames}
      itemStates={itemStates}
      items={plainGroup.children}
      selectedItem={selectedItem}
      itemControls={itemControls}
      dragInProcess={dragInProcess}
      getItemId={getItemId}
      getItemForm={getItemForm}
      getItemLabel={getItemLabel}
      isItemSortable={isItemSortable}
      getItemPlaceholder={getItemPlaceholder}
      onRearrange={onRearrange}
      updateItemState={updateItemState}
    />
  ) : (
    groupsWithChildren?.map((group) => {
      const groupId = getGroupId(group);

      if (!group.children?.length && hideEmpty) {
        return null;
      }

      return (
        <GroupWithChildren
          key={groupId || null}
          group={group}
          groups={groups}
          dndEnabled={dndEnabled}
          dragInProcess={dragInProcess}
          classNames={classNames}
          itemControls={itemControls}
          itemStates={itemStates}
          selectedItem={selectedItem}
          groupControls={groupControls}
          groupState={groupStates?.[groupId]}
          getItemId={getItemId}
          getGroupId={getGroupId}
          getItemForm={getItemForm}
          getItemLabel={getItemLabel}
          getGroupForm={getGroupForm}
          getGroupLabel={getGroupLabel}
          getGroupError={getGroupError}
          getItemPlaceholder={getItemPlaceholder}
          onExpand={onExpand}
          onCollapse={onCollapse}
          onRearrange={onRearrange}
          isItemSortable={isItemSortable}
          updateItemState={updateItemState}
          updateGroupState={updateGroupState}
        />
      );
    })
  );

  return (
    <div className={classNames.containerClassName}>
      {showPlaceholder && (placeholder || <div className="padding text-center">{translate('No entities found!')}</div>)}

      {content}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(GroupedList);
